import { useEffect, useRef } from 'react';

/**
 * Kleiner App-Event-Bus (erstes Pub/Sub der Codebase): View-übergreifende
 * Änderungs-Signale, z. B. damit die gemountet gehaltene Listen-Spalte
 * (ModuleColumns) nach Create/Update/Statuswechsel still nachlädt.
 *
 * Konvention: nur in Promise-/await-Callbacks emittieren (nie während des
 * Renderings), Handler im useAppEvent sind Fire-and-Forget (Fetches).
 */
export type AppEventName = 'bauvorhaben:changed' | 'rechnungen:changed' | 'dokumente:changed' | 'einkauf:changed';

const listeners = new Map<AppEventName, Set<() => void>>();

export function emitAppEvent(name: AppEventName): void {
  const set = listeners.get(name);
  if (!set) return;
  for (const listener of Array.from(set)) {
    try {
      listener();
    } catch {
      // Ein fehlschlagender Listener darf die anderen nicht stoppen.
    }
  }
}

/**
 * Abonnement mit ref-stabilem Handler: der neueste Handler wird über eine
 * Ref aufgerufen, das Set-Abo bleibt pro [name] stabil — sonst würde jede
 * Render-Änderung des Consumers neu abonnieren (und Emits doppelt feuern).
 */
export function useAppEvent(name: AppEventName, handler: () => void): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const wrapper = () => handlerRef.current();
    let set = listeners.get(name);
    if (!set) {
      set = new Set();
      listeners.set(name, set);
    }
    set.add(wrapper);
    return () => {
      set.delete(wrapper);
    };
  }, [name]);
}